import { createContext, useEffect, useState, type ReactNode } from "react";

type OrderType = "exchpart" | "sellpart";

export interface CartItem {
  goid: string;
  quantity: number;
  table: string;
  typeorder: OrderType;
}

interface CartContextProps {
  cart: CartItem[];
  addToCart: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeFromCart: (goid: string, typeorder: OrderType) => void;
  clearCart: () => void;
  increaseQuantity: (goid: string, typeorder: OrderType) => void;
  decreaseQuantity: (goid: string, typeorder: OrderType) => void;
}

const CART_KEY = "hixa_shopping_cart";

export const CartContext = createContext<CartContextProps | undefined>(
  undefined
);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);

  // Завантаження з localStorage при ініціалізації
  useEffect(() => {
    const storedCart = localStorage.getItem(CART_KEY);
    if (storedCart) {
      setCart(JSON.parse(storedCart));
    }
  }, []);

  // Збереження в localStorage
  const saveCart = (items: CartItem[]) => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
    setCart(items);
  };

  // Додай параметр quantity з урахуванням typeorder
  const addToCart = (
    item: Omit<CartItem, "quantity">,
    quantity: number = 1
  ) => {
    // Перевіряємо чи вже є товар з таким goid і typeorder
    const existing = cart.find(
      (i) => i.goid === item.goid && i.typeorder === item.typeorder
    );
    if (existing) {
      // Якщо такий товар є, оновлюємо кількість
      const updated = cart.map((i) =>
        i.goid === item.goid && i.typeorder === item.typeorder
          ? { ...i, quantity: i.quantity + quantity }
          : i
      );
      saveCart(updated);
    } else {
      // Якщо товару немає в кошику, додаємо новий
      saveCart([...cart, { ...item, quantity }]);
    }
  };

  // Збільшити кількість з урахуванням typeorder
  const increaseQuantity = (goid: string, typeorder: OrderType) => {
    const updated = cart.map((item) =>
      item.goid === goid && item.typeorder === typeorder
        ? { ...item, quantity: item.quantity + 1 }
        : item
    );
    saveCart(updated);
  };

  // Зменшити кількість (і видалити, якщо quantity = 1)
  const decreaseQuantity = (goid: string, typeorder: OrderType) => {
    const updated = cart
      .map((item) =>
        item.goid === goid && item.typeorder === typeorder
          ? { ...item, quantity: item.quantity - 1 }
          : item
      )
      .filter((item) => item.quantity > 0);

    saveCart(updated);
  };

  // Видалити товар з конкретним typeorder
  const removeFromCart = (goid: string, typeorder: OrderType) => {
    const updated = cart.filter(
      (item) => item.goid !== goid || item.typeorder !== typeorder
    );
    saveCart(updated);
  };

  const clearCart = () => {
    saveCart([]);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        clearCart,
        increaseQuantity,
        decreaseQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
